import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Routes } from '@angular/router';
import { CompanyComponentsModule } from '@tix/company/components';
import { SharedCompanyStateModule } from '@tix/company/state';
import { getSelectedName } from '@tix/company/state/selectors';
import { getSelectedEventName, TixEventsStateModule } from '@tix/events/state';
import {
  TixCompanyLayoutComponent,
  TixHomeLayoutComponent,
  TixLayoutModule,
  TixPublicLayoutComponent
} from '@tix/layout';
import {
  Breadcrumb,
  HeroArrowLeftSolid,
  HeroCheckCircle,
  HeroPlusSolid,
  HeroUser,
  HeroUserGroup,
  HeroUserGroupSolid,
  HeroUserSolid,
  LoadingStateModule,
  PendingChangesGuard,
  PendingChangesGuardModule,
  TixAddressAdminModule,
  TixButtonModule,
  TixCardModule,
  TixDomainModule,
  TixIconModule,
  TixIconRegistry,
  TixInputModule,
  TixMediaFilesAdminModule,
  TixPhoneNumbersAdminModule,
  TixSocialMediasAdminModule
} from '@tix/shared/ui/components';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TixCommunicationsAdminModule } from '@tix/shared/ui/components';
import { TixVenuesStateModule } from '@tix/venues/state';
import { TixCompanyAdminPage } from './company-admin/company-admin.component';
import { TixCheckTicketPageComponent } from './check-ticket/check-ticket.component';
import { TixCompanyListPage } from './company-list/company-list.component';
import { WarningComponentModule } from '../../../../shared/ui/components/src/lib/warning/warning.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { TixAuthorizedGuard } from '@tix/auth/guards';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { PermissionsGuard, Permissions } from '@tix/auth/state';
import { NightlyCloseoutReportComponent } from './reports/pages/nightly-closeout-report/nightly-closeout-report.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';

const angularMaterialModules = [
  MatProgressSpinnerModule,
  MatSnackBarModule,
  MatExpansionModule,
  MatButtonModule,
  MatTooltipModule,
  MatTabsModule
];

const BREADCRUMBS: { [key: string]: Breadcrumb } = {
  COMPANY: { label: 'Companies' },
  COMPANY_ID: { labelSelector: getSelectedName },
  OVERVIEW: { label: 'Overview' },
  VENUES: { label: 'Venues' },
  VENUES_ID: { label: 'Boston Club' },
  EVENTS: { label: 'Events' },
  EVENTS_ID: { labelSelector: getSelectedEventName },
  STAFF: { label: 'Staff' },
  STAFF_ID: { label: '123' },
  CUSTOMER: { label: 'Customers' },
  DASHBOARD: { label: 'Dashboard' },
  DISCOUNTS: { label: 'Discounts' },
  COMMUNICATIONS: { label: 'Communications' }
};

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'company'
    // component: TixHomeLayoutComponent
  },
  {
    path: 'check-ticket',
    component: TixCheckTicketPageComponent,
    canActivate: [TixAuthorizedGuard]
  },
  {
    path: 'company',
    data: { breadcrumb: BREADCRUMBS.COMPANY },
    children: [
      {
        path: '',
        component: TixPublicLayoutComponent,
        data: {
          breadcrumbSkip: true,
          hideRightSection: true // This will hide the right section
        },
        children: [
          {
            path: '',
            component: TixCompanyListPage
          },
          {
            path: 'add',
            component: TixCompanyAdminPage
          }
        ]
      },
      {
        path: ':companyId',
        component: TixCompanyLayoutComponent,
        data: { breadcrumb: BREADCRUMBS.COMPANY_ID },
        children: [
          {
            path: '',
            component: TixCompanyAdminPage,
            canDeactivate: [PendingChangesGuard],
            canActivate: [PermissionsGuard],
            data: {
              breadcrumb: BREADCRUMBS.OVERVIEW,
              permissions: [
                Permissions.DELETE_COMPANY,
                Permissions.EDIT_COMPANY
              ]
            }
          },
          {
            path: 'Add-Ons',
            canActivate: [PermissionsGuard],
            loadChildren: () =>
              import('@tix/feature-addons').then(m => m.FeatureAddonsModule),
            data: {
              breadcrumb: {
                label: 'Add-Ons'
              },
              permissions: [Permissions.EDIT_ADDONS, Permissions.DELETE_ADDONS]
            }
          },
          {
            path: 'venues',
            loadChildren: () =>
              import('@tix/venues/pages').then(
                module => module.TixVenuesPagesModule
              ),
            data: { breadcrumb: BREADCRUMBS.VENUES, permissions: [] }
          },
          {
            path: 'events',
            loadChildren: () =>
              import('@tix/events/pages').then(
                module => module.TixEventsPagesModule
              ),
            data: { breadcrumb: BREADCRUMBS.EVENTS }
          },
          {
            path: 'discounts',
            canActivate: [PermissionsGuard],
            loadChildren: () =>
              import('@tix/discounts').then(module => module.DiscountsModule),
            data: {
              breadcrumb: BREADCRUMBS.DISCOUNTS,
              permissions: [
                Permissions.EDIT_DISCOUNT,
                Permissions.DELETE_DISCOUNT
              ]
            }
          },
          {
            path: 'staff',
            loadChildren: () =>
              import('@tix/staff/pages').then(
                module => module.StaffPagesModule
              ),
            data: { breadcrumb: BREADCRUMBS.STAFF }
          },
          {
            path: 'customers',
            loadChildren: () =>
              import('@tix/customer/pages').then(
                module => module.CustomerPagesModule
              ),
            data: { breadcrumb: BREADCRUMBS.CUSTOMER }
          },
          {
            path: 'dashboard',
            loadChildren: () =>
              import('@tix/dashboard/pages').then(
                module => module.DashboardPagesModule
              ),
            data: { breadcrumb: BREADCRUMBS.DASHBOARD }
          },
          {
            path: 'communications',
            loadChildren: () =>
              import('@tix/communications/pages').then(
                module => module.CommunicationsPagesModule
              ),
            data: { breadcrumb: BREADCRUMBS.COMMUNICATIONS }
          },
          {
            path: 'reports',
            loadChildren: () =>
              import('./reports/reports.module').then(
                module => module.TixReportsModule
              ),
            data: { breadcrumb: { label: 'Financial Reports' } }
          },
          {
            path: 'finance',
            loadChildren: () =>
              import('@tix/finance').then(module => module.TixFinanceModule)
          },
          {
            path: 'account',
            loadChildren: () =>
              import('@tix/auth/pages').then(
                module => module.TixAuthPagesModule
              ),
            data: {
              breadcrumb: { label: 'My Profile' },
              isProfileRoute: true
            }
          }
        ]
      }
    ]
  }
];

@NgModule({
  imports: [
    CommonModule,
    CdkTableModule,
    TixLayoutModule,
    TixButtonModule,
    TixCardModule,
    TixIconModule,
    MatFormFieldModule,
    MatInputModule,
    TixInputModule,
    TixVenuesStateModule,
    TixPhoneNumbersAdminModule,
    TixSocialMediasAdminModule,
    TixMediaFilesAdminModule,
    TixAddressAdminModule,
    ReactiveFormsModule,
    CompanyComponentsModule,
    SharedCompanyStateModule,
    TixEventsStateModule,
    RouterModule.forChild(routes),
    TixDomainModule,
    LoadingStateModule,
    ...angularMaterialModules,
    WarningComponentModule,
    PendingChangesGuardModule,
    TixCommunicationsAdminModule
  ],
  declarations: [
    TixCompanyAdminPage,
    TixCompanyListPage,
    TixCheckTicketPageComponent,
    NightlyCloseoutReportComponent
  ]
})
export class CompanyPagesModule {
  constructor(private iconRegistry: TixIconRegistry) {
    iconRegistry.addSvgIconConfigs([
      HeroPlusSolid,
      HeroUserSolid,
      HeroUserGroupSolid,
      HeroCheckCircle
    ]);
    iconRegistry.addSvgIconConfigs([HeroArrowLeftSolid]);
  }
}
